<template>
    <div v-if="user && !loading" class="row">
        <div class="col-12">
            <button
                type="button"
                class="btn btn-primary float-right"
                @click="updateUser"
            >
                {{ $t('save') }}
            </button>

            <h1 v-if="user">{{ $t("edit") + ` ${user.display_name ? user.display_name : ''}` }}</h1>
        </div>

        <div class="col-12 col-lg-8 col-xxl-6 mb-5 pr-lg-5">

            <div class="alert alert-info clearfix" v-if="user.status == 'pending'">
                {{$t('this-user-has-not-responded-invite-resent-question')}}
                <button @click="resendInvite()" class="btn btn-outline-secondary float-right">{{ $t('send') }}</button>
            </div>

            <div v-if="updateMsg" class="alert alert-success">{{ updateMsg }}</div>

            <!-- Profile -->
            <ProfileEditor :user="user" :validate="validate" class="mt-4" />

            <!-- Student -->
            <StudentEditor
                v-if="isStudent"
                v-model="student"
                class="mt-5"
            />

            <!-- Teacher -->
            <TeacherEditor
                v-if="isTeacher"
                v-model="teacher"
                class="mt-5"
            />

            <!-- Notes -->
            <TextAreaAutosize :label="$t('notes')" v-model="user.notes" />

            <!-- Flags -->
            <FlagsEditor :user="user" class="mt-5" />

            <!-- Roles -->
            <RolesEditor :user="user" :roles="roles" class="mt-5" />
        </div>

        <!-- Image & Password -->
        <div class="col-12 col-lg-4 col-xxl-3 pl-lg-0">

            <!-- Status -->
            <h3 class="mt-5 mb-3">{{ $t('user-status') }}</h3>
            <SelectInput
                v-model="user.status"
                :options="['active', 'suspended', 'closed', 'pending']"
				:labelCb="(option) => { return this.$t(option); }"
                :label="$t('status')"
            />

            <!-- Profile Image -->
            <AvatarEditor :userId="this.user.id" />

            <!-- Password -->
            <PasswordEditor :userId="this.user.id" class="mt-5" />

        </div>
    </div>
</template>

<script>
import StudentEditor from 'src/components/Users/StudentEditor.vue';
import TeacherEditor from 'src/components/Users/TeacherEditor.vue';
import FlagsEditor from 'src/components/Users/FlagsEditor.vue';
import RolesEditor from 'src/components/Users/RolesEditor.vue';
import AvatarEditor from 'src/components/Users/AvatarEditor.vue';
import PasswordEditor from 'src/components/Users/PasswordEditor.vue';
import ProfileEditor from 'src/components/Users/ProfileEditor.vue';

export default {
    data() {
        return {
            user: undefined,
            student: undefined,
            teacher: undefined,
            validate: false,
            updateMsg: '',
            loading: true,
            submitting: false,
            roles: []
        }
    },
    mounted() {
        this.allRoles()
        this.fetchUser().finally(() => this.loading = false)
    },
    methods: {
        async fetchUser() {
            try {
                this.$store.commit('showBlockingSpinner')

                const res = await this.$cfetch(`${this.$settings.APIBaseURL}/users/get`, {
                    method: 'get',
                    data: { id: this.$route.params.userId },
                })

                if (res.status !== 'SUCCESS') {
                    this.$store.commit("openDialog", {
                        title: this.$t("something-went-wrong"),
                        content: this.$t('user-not-found'),
                        confirmCallBack: this.$router.push('/settings/users')
                    })
                    return
                }

                this.user = res.user
                this.$nextTick(() => {
    				this.$emit('dataChanged', false);
    			});

                this.user.student ? this.student = this.user.student : this.student = {}
                this.user.teacher ? this.teacher = this.user.teacher : this.teacher = {}
            }
            catch (err) {
                this.$log.error.call(this, err)
            }
            finally {
                this.$store.commit('hideBlockingSpinner')
            }
        },
        async updateUser() {
            if (this.submitting) return
            this.submitting = true

            this.validate = true
            this.errors = false

            if (!this.user.first_name) this.errors = true
            if (!this.user.last_name) this.errors = true
            if (!this.user.display_name) this.errors = true
            if (!this.user.email) this.errors = true
            if (!this.user.username) this.errors = true

            if (this.errors) return

            try {
                this.updateMsg = ''
                this.$store.commit('showBlockingSpinner')

                const res = await this.$cfetch(`${this.$settings.APIBaseURL}/users/update`, {
                    method: 'post',
                    data: {
                        ...this.user,
                        student: this.isStudent ? JSON.stringify(this.student) : undefined,
                        teacher: this.isTeacher ? JSON.stringify(this.teacher) : undefined,
                        roles: JSON.stringify(this.user.roles),
                        flags: JSON.stringify(this.user.flags),
                        user_username: this.user.username,
                    },
                })

                if (res.status === 'SUCCESS') {
                    this.validate = false
                    this.updateMsg = this.$t('user-updated')
					this.$nextTick(() => {
						this.$emit('dataChanged', false);
					});
                    return
                }

                if (res.errorType === 'ERROR_USERNAME_IN_USE') {
                    this.$store.commit("openDialog", {
                        title: this.$t("username-taken"),
                        content: this.$t('sorry-username-in-use'),
                        confirmCallBack: function () {}
                    })
                } else if (res.errorType === 'ERROR_EMAIL_IN_USE') {
                    this.$store.commit("openDialog", {
                        title: this.$t("email-taken"),
                        content: this.$t('sorry-account-exists-for-email'),
                        confirmCallBack: function () {}
                    })
                } else {
                    this.$store.commit("openDialog", {
                        title: this.$t("something-went-wrong"),
                        content: this.$t('user-update-failed'),
                        confirmCallBack: function () {}
                    })
                }
            } catch (err) {
                this.$log.error.call(this, err)
            } finally {
                this.$store.commit('hideBlockingSpinner')
            }
        },
        resendInvite() {
			this.$cfetch(`${this.$settings.APIBaseURL}/users/invite`, {
				method: 'post',
				data: {
					id: this.user.id
				},
			}).then(result => {
				 if (result.status === 'SUCCESS') {
                    this.$store.commit("openDialog", {
                        title: this.$t("invite-sent"),
                        content: this.$t('user-invite-email-sent')
                    })
				} else {
					this.$store.commit("openDialog", {
						title: this.$t('oops'),
						content: this.$t('something-went-wrong')
					});
				 }
			}).catch(err => {
                this.$log.error.call(this, err);
				this.$store.commit("openDialog", {
					title: this.$t('oops'),
					content: this.$t('something-went-wrong')
				})
			});
        },
        async allRoles() {
            try {
                this.$store.commit('showBlockingSpinner')

                const res = await this.$cfetch(`${this.$settings.APIBaseURL}/roles/all`, {
                    method: 'get'
                })

                if (res.status !== 'SUCCESS') return;

                this.roles = res.roles;
            }
            catch (err) {
                this.$log.error.call(this, err)
            }
            finally {
                this.$store.commit('hideBlockingSpinner')
            }
        },
    },
    computed: {
        isStudent() {
            if (!this.user) return false;
            var student = false;
            this.roles.filter(role => role.student === true).forEach(item => {
            	if(this.user.roles.includes(item.id)){
            		student = true;
            	}
            });
            return student;
        },
        isTeacher() {
            if (!this.user) return false;
            var teacher = false;
            this.roles.filter(role => role.teacher === true).forEach(item => {
            	if(this.user.roles.includes(item.id)){
            		teacher = true;
            	}
            });
            return teacher;
        },
    },
    watch: {
        userId: function () {
            this.user = null
            this.fetchUser()
        },
    	user: {
			handler(){
				this.$emit('dataChanged', true);
			},
			deep: true
    	}
    },
    components: {
        StudentEditor,
        TeacherEditor,
        FlagsEditor,
        RolesEditor,
        AvatarEditor,
        PasswordEditor,
        ProfileEditor
    }
}
</script>
